import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import {
    CognitoIdentityProviderClient,
    SignUpCommand,
    InitiateAuthCommand,
    AuthFlowType,
} from '@aws-sdk/client-cognito-identity-provider';

/**
 * Cognito identity provider identifiers.
 * These must match the provider names registered in the Cognito User Pool.
 */
export type CognitoProvider = 'Google' | 'GitHub' | 'LoginWithAmazon' | 'Microsoft';

export interface CognitoTokenResponse {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    token_type: string;
}

export interface CognitoUser {
    sub: string;
    email: string;
    email_verified?: boolean;
    name?: string;
    given_name?: string;
    family_name?: string;
    picture?: string;
    identities?: string; // JSON string of identity provider info
}

interface StoredSession {
    idToken: string;
    accessToken: string;
    refreshToken?: string;
    user: CognitoUser;
    expiresAt: number; // epoch ms
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private static readonly SESSION_KEY = 'wg_session';

    private readonly cognitoConfig = environment.cognito;
    private readonly tokenEndpoint = `https://${this.cognitoConfig.domain}/oauth2/token`;
    private readonly authorizeEndpoint = `https://${this.cognitoConfig.domain}/oauth2/authorize`;
    private readonly logoutEndpoint = `https://${this.cognitoConfig.domain}/logout`;
    private readonly userInfoEndpoint = `https://${this.cognitoConfig.domain}/oauth2/userInfo`;

    private readonly cognitoClient = new CognitoIdentityProviderClient({
        region: this.cognitoConfig.userPoolId.split('_')[0], // Extract region from pool ID
    });

    /** Reactive auth state */
    private _user = signal<CognitoUser | null>(null);
    private _loading = signal<boolean>(false);
    private _error = signal<string | null>(null);

    /** Public readonly signals */
    readonly user = this._user.asReadonly();
    readonly loading = this._loading.asReadonly();
    readonly error = this._error.asReadonly();
    readonly isAuthenticated = computed(() => this._user() !== null && !this.isTokenExpired());

    constructor(
        private http: HttpClient,
        private router: Router,
    ) {
        this.restoreSession();
    }

    // ─── OAuth Login Flow ──────────────────────────────────────────

    /**
     * Redirect the user to Cognito's Hosted UI for a specific identity provider.
     * This skips the Hosted UI provider-selection screen and goes straight to the provider.
     */
    loginWithProvider(provider: CognitoProvider): void {
        const params = new URLSearchParams({
            client_id: this.cognitoConfig.clientId,
            response_type: this.cognitoConfig.responseType,
            scope: this.cognitoConfig.scopes.join(' '),
            redirect_uri: this.cognitoConfig.redirectUri,
            identity_provider: provider,
        });

        window.location.href = `${this.authorizeEndpoint}?${params.toString()}`;
    }

    /**
     * Convenience methods per provider.
     */
    loginWithGoogle(): void {
        this.loginWithProvider('Google');
    }
    loginWithGithub(): void {
        this.loginWithProvider('GitHub');
    }
    loginWithAmazon(): void {
        this.loginWithProvider('LoginWithAmazon');
    }
    loginWithMicrosoft(): void {
        this.loginWithProvider('Microsoft');
    }

    // ─── Email/Password Signup ─────────────────────────────────────

    /**
     * Sign up a new user with email and password.
     * Cognito will send a verification code to the email.
     */
    async signUpWithEmail(email: string, password: string, name: string): Promise<void> {
        this._loading.set(true);
        this._error.set(null);

        try {
            const command = new SignUpCommand({
                ClientId: this.cognitoConfig.clientId,
                Username: email,
                Password: password,
                UserAttributes: [
                    {
                        Name: 'email',
                        Value: email,
                    },
                    {
                        Name: 'name',
                        Value: name,
                    },
                ],
            });

            await this.cognitoClient.send(command);
            this._loading.set(false);

            // Success - user needs to verify email
            // The calling component will handle navigation
        } catch (err: any) {
            const message = this.getCognitoErrorMessage(err);
            this._error.set(message);
            this._loading.set(false);
            throw new Error(message);
        }
    }

    /**
     * Log in with email and password.
     */
    async loginWithEmail(email: string, password: string): Promise<void> {
        this._loading.set(true);
        this._error.set(null);

        try {
            const command = new InitiateAuthCommand({
                ClientId: this.cognitoConfig.clientId,
                AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
                AuthParameters: {
                    USERNAME: email,
                    PASSWORD: password,
                },
            });

            const response = await this.cognitoClient.send(command);

            if (!response.AuthenticationResult) {
                throw new Error('Authentication failed');
            }

            // Extract user info from ID token instead of calling userInfo endpoint
            const userInfo = this.decodeIdToken(response.AuthenticationResult.IdToken!);

            // Store session
            const expiresAt = Date.now() + (response.AuthenticationResult.ExpiresIn || 3600) * 1000;
            const session: StoredSession = {
                idToken: response.AuthenticationResult.IdToken!,
                accessToken: response.AuthenticationResult.AccessToken!,
                refreshToken: response.AuthenticationResult.RefreshToken,
                user: userInfo,
                expiresAt,
            };

            localStorage.setItem(AuthService.SESSION_KEY, JSON.stringify(session));
            this._user.set(userInfo);
            this._loading.set(false);

            this.router.navigate(['/dashboard']);
        } catch (err: any) {
            const message = this.getCognitoErrorMessage(err);
            this._error.set(message);
            this._loading.set(false);
            throw new Error(message);
        }
    }

    /**
     * Convert Cognito error codes to user-friendly messages
     */
    private getCognitoErrorMessage(err: any): string {
        const errorCode = err.name || err.code;

        switch (errorCode) {
            case 'UsernameExistsException':
                return 'An account with this email already exists';
            case 'InvalidPasswordException':
                return 'Password does not meet requirements';
            case 'InvalidParameterException':
                return 'Invalid email or password format';
            case 'TooManyRequestsException':
                return 'Too many attempts. Please try again later';
            case 'NotAuthorizedException':
                return 'Incorrect email or password';
            case 'UserNotConfirmedException':
                return 'Please verify your email before signing in';
            case 'UserNotFoundException':
                return 'No account found with this email';
            default:
                return err.message || 'Authentication failed';
        }
    }

    /**
     * Redirect to Cognito Hosted UI without pre-selecting a provider.
     * Shows the Cognito-hosted sign-in page with all provider options.
     */
    loginWithHostedUI(): void {
        const params = new URLSearchParams({
            client_id: this.cognitoConfig.clientId,
            response_type: this.cognitoConfig.responseType,
            scope: this.cognitoConfig.scopes.join(' '),
            redirect_uri: this.cognitoConfig.redirectUri,
        });

        window.location.href = `${this.authorizeEndpoint}?${params.toString()}`;
    }

    // ─── OAuth Callback ────────────────────────────────────────────

    /**
     * Handle the Cognito redirect callback.
     * Exchanges the authorization code for tokens, fetches user info,
     * and stores the session.
     */
    async handleCallback(code: string): Promise<void> {
        this._loading.set(true);
        this._error.set(null);

        try {
            // Exchange authorization code for tokens
            const body = new HttpParams()
                .set('grant_type', 'authorization_code')
                .set('client_id', this.cognitoConfig.clientId)
                .set('code', code)
                .set('redirect_uri', this.cognitoConfig.redirectUri);

            const tokenResponse = await firstValueFrom(
                this.http.post<CognitoTokenResponse>(this.tokenEndpoint, body.toString(), {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                }),
            );

            // Extract user info from ID token (JWT)
            // The ID token contains all user claims, so we don't need to call /userInfo
            const userInfo = this.decodeIdToken(tokenResponse.id_token);

            // Store session
            const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
            const session: StoredSession = {
                idToken: tokenResponse.id_token,
                accessToken: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                user: userInfo,
                expiresAt,
            };

            localStorage.setItem(AuthService.SESSION_KEY, JSON.stringify(session));
            this._user.set(userInfo);
            this._loading.set(false);

            this.router.navigate(['/dashboard']);
        } catch (err: any) {
            const message =
                err?.error?.error_description ||
                err?.error?.error ||
                err?.message ||
                'Authentication failed';
            this._error.set(message);
            this._loading.set(false);
            this.router.navigate(['/login']);
        }
    }

    // ─── Logout ────────────────────────────────────────────────────

    /**
     * Log the user out — clears local session and redirects to Cognito logout.
     */
    logout(): void {
        this.clearSession();

        const params = new URLSearchParams({
            client_id: this.cognitoConfig.clientId,
            logout_uri: this.cognitoConfig.logoutUri,
        });

        window.location.href = `${this.logoutEndpoint}?${params.toString()}`;
    }

    // ─── Token Accessors ──────────────────────────────────────────

    /**
     * Get the Cognito ID token (used for API authorization).
     */
    getIdToken(): string | null {
        const session = this.getStoredSession();
        return session?.idToken ?? null;
    }

    /**
     * Get the Cognito access token.
     */
    getAccessToken(): string | null {
        const session = this.getStoredSession();
        return session?.accessToken ?? null;
    }

    /**
     * Check if the stored token is expired.
     */
    isTokenExpired(): boolean {
        const session = this.getStoredSession();
        if (!session) return true;
        return Date.now() >= session.expiresAt;
    }

    /**
     * Clear any auth error state.
     */
    clearError(): void {
        this._error.set(null);
    }

    // ─── Private Helpers ───────────────────────────────────────────

    private getStoredSession(): StoredSession | null {
        const raw = localStorage.getItem(AuthService.SESSION_KEY);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as StoredSession;
        } catch {
            return null;
        }
    }

    private restoreSession(): void {
        const session = this.getStoredSession();
        if (session && Date.now() < session.expiresAt) {
            this._user.set(session.user);
        } else if (session) {
            // Expired — clean up
            this.clearSession();
        }
    }

    private clearSession(): void {
        localStorage.removeItem(AuthService.SESSION_KEY);
        this._user.set(null);
    }

    /**
     * Decode the ID token (JWT) to extract user claims.
     * The ID token is a base64-encoded JWT with three parts: header.payload.signature
     */
    private decodeIdToken(idToken: string): CognitoUser {
        try {
            // Split the JWT and decode the payload (second part)
            const parts = idToken.split('.');
            if (parts.length !== 3) {
                throw new Error('Invalid JWT format');
            }

            // Decode base64url payload
            const payload = parts[1];
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join(''),
            );

            const claims = JSON.parse(jsonPayload);

            // Map JWT claims to CognitoUser interface
            return {
                sub: claims.sub,
                email: claims.email,
                email_verified: claims.email_verified,
                name: claims.name,
                given_name: claims.given_name,
                family_name: claims.family_name,
                picture: claims.picture,
                identities: claims.identities,
            };
        } catch (error) {
            console.error('Failed to decode ID token:', error);
            throw new Error('Invalid ID token');
        }
    }
}
